import React, { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { displayFinancialSource, formatSourcesLine } from './sourceLabels';

/**
 * Layer 4 — Methodology & provenance, collapsed to a single line.
 * Risk-relevant caveats stay visible on a thin amber strip even when
 * the section is closed, so nothing material is ever hidden.
 */
const MethodologyDisclosure = ({ company }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { analysis } = company;
  const { dataLineage, dataQuality, distressScore, aiAnalysis, inputValidation } = analysis;

  const mixedVintage = useMemo(() => {
    const parse = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    const marketAsOf = parse(dataLineage?.marketAsOf);
    const periodEnd = parse(dataLineage?.financialPeriodEnd);
    if (!marketAsOf || !periodEnd) return null;
    const diffDays = Math.abs(marketAsOf.getTime() - periodEnd.getTime()) / 86400000;
    return diffDays > 183
      ? `Annual statements (ended ${dataLineage.financialPeriodEnd}) are paired with market data as of ${dataLineage.marketAsOf}.`
      : null;
  }, [dataLineage]);

  const caveats = useMemo(() => {
    const list = [];
    if (mixedVintage) list.push(mixedVintage);
    (dataLineage?.sourceCaveats || []).forEach((c) => list.push(c));
    (inputValidation?.warnings || []).forEach((w) => list.push(w));
    return list;
  }, [mixedVintage, dataLineage, inputValidation]);

  if (!dataLineage) return null;

  const basisSummary = [
    dataLineage.financialPeriodEnd ? `Statements ended ${dataLineage.financialPeriodEnd}` : null,
    dataLineage.filingForm
      ? `${dataLineage.filingForm}${dataLineage.accountingStandard ? ` (${dataLineage.accountingStandard})` : ''}${
          dataLineage.statementCurrency ? `, ${dataLineage.statementCurrency}` : ''
        }`
      : dataLineage.ownership === 'private'
        ? 'Company-provided financials'
        : displayFinancialSource(dataLineage.financialDataSource),
    caveats.length > 0 ? `${caveats.length} ${caveats.length === 1 ? 'caveat' : 'caveats'}` : null
  ]
    .filter(Boolean)
    .join(' \u00b7 ');

  const rows = [
    {
      key: 'Data as of',
      val: dataLineage.marketAsOf || analysis.calculationDate || 'N/A'
    },
    {
      key: 'Statement period ended',
      val: dataLineage.financialPeriodEnd || 'N/A'
    },
    {
      key: 'Sources',
      val: formatSourcesLine(dataLineage.financialDataSource, dataLineage.marketDataSource)
    },
    {
      key: 'Scoring model',
      val: dataLineage.altmanFormulaVariant || distressScore.formulaLabel || distressScore.formula
    },
    {
      key: 'Methodology version',
      val: aiAnalysis?.methodologyVersion || dataLineage.methodologyVersion || '—'
    },
    {
      key: 'Rubric version',
      val: aiAnalysis?.rubricVersion || dataLineage.rubricVersion || '—'
    },
    dataLineage.ownership
      ? {
          key: 'Ownership',
          val: `${dataLineage.ownership === 'private' ? 'Private (company-provided financials)' : 'Public (listed)'}${
            dataLineage.dataMode === 'limited' ? ' \u2014 limited-data mode' : ''
          }`
        }
      : null,
    dataLineage.filingForm
      ? {
          key: 'Filing basis',
          val: `${dataLineage.filingForm}${dataLineage.accountingStandard ? ` (${dataLineage.accountingStandard})` : ''}${
            dataLineage.statementCurrency ? ` \u2014 reported in ${dataLineage.statementCurrency}` : ''
          }`
        }
      : null,
    dataQuality
      ? { key: 'Data completeness', val: `${dataQuality.level} (${dataQuality.score}%)` }
      : null,
    {
      key: 'Composition',
      val: 'Figures are read from the filing. Ratios are computed from those figures. The sentences follow from that judgment.'
    },
    {
      key: 'Trend basis',
      val: 'Year to year, against this company\u2019s own reported history'
    }
  ].filter(Boolean);

  return (
    <div id="methodology">
      <button
        type="button"
        className="fr-disclosure-toggle"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <ChevronRight size={14} className="fr-chevron" />
        Methodology &amp; data sources
        <span className="fr-disclosure-summary">{basisSummary}</span>
      </button>

      {!isOpen && caveats.length > 0 && (
        <div className="fr-caveat-strip">
          <span>
            {caveats[0]}
            {caveats.length > 1 ? ` (+${caveats.length - 1} more; expand for details)` : ''}
          </span>
        </div>
      )}

      {isOpen && (
        <div className="fr-disclosure-body">
          {rows.map((row) => (
            <div key={row.key} className="fr-method-row">
              <span className="fr-method-key">{row.key}</span>
              <span className="fr-method-val fr-num">{row.val}</span>
            </div>
          ))}

          {caveats.length > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              {caveats.map((caveat, index) => (
                <div key={index} className="fr-caveat-strip" style={{ margin: '0 0 0.5rem' }}>
                  <span>{caveat}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MethodologyDisclosure;
